export interface RuntimeConfig {
  environment: string;
  oidc: {
    authority: string;
    clientId: string;
    scope: string;
  };
  apis: {
    dam: string;
    docs: string;
    sftpgo: string;
    masterdata: string;
    mail: string;
  };
  demoMode: boolean;
}

declare global {
  interface Window {
    /**
     * Ein Deployment liefert nur die Werte, die es überschreibt - `public/runtime-config.js` etwa
     * nennt unter `apis` allein die DAM-Basis-URL. `Partial<RuntimeConfig>` macht davon nur die
     * beiden Zweige selbst optional und lässt ihre Felder Pflicht, weshalb sie hier ausgenommen
     * und eigens als teilweise beschrieben werden. Ohne das müsste jede Stelle, die eine
     * Konfiguration setzt, bei jedem neuen Modul um ein Feld ergänzt werden, das sie nicht braucht.
     */
    __GES_PORTAL_CONFIG__?: Omit<Partial<RuntimeConfig>, 'oidc' | 'apis'> & {
      oidc?: Partial<RuntimeConfig['oidc']>;
      apis?: Partial<RuntimeConfig['apis']>;
    };
  }
}

const supplied = window.__GES_PORTAL_CONFIG__ ?? {};
const demoOverride = import.meta.env.VITE_DEMO_MODE;

function absoluteApiUrl(value: string) {
  const url = new URL(value, window.location.origin);
  return url.toString().replace(/\/?$/, '/');
}

export const runtimeConfig: RuntimeConfig = Object.freeze({
  environment: supplied.environment || 'local',
  oidc: {
    authority: supplied.oidc?.authority?.replace(/\/$/, '') || '',
    clientId: supplied.oidc?.clientId || 'ges-unified-frontend',
    scope: supplied.oidc?.scope || 'openid profile email dam.read dam.write dam.export dam.admin docs.read docs.write docs.analytics docs.admin sftp.read sftp.write sftp.webhooks sftp.admin std.read std.write std.edit std.manage std.upstream-update std.admin mail.read mail.write mail.send mail.admin dam.apidocs docs.apidocs std.apidocs mail.apidocs sftp.apidocs ges.portal offline_access',
  },
  apis: {
    dam: absoluteApiUrl(supplied.apis?.dam || '/api/dam/'),
    docs: absoluteApiUrl(supplied.apis?.docs || '/api/docs/'),
    sftpgo: absoluteApiUrl(supplied.apis?.sftpgo || '/api/sftpgo/'),
    masterdata: absoluteApiUrl(supplied.apis?.masterdata || '/api/masterdata/'),
    // Empty on purpose where mail-service is not attached (compose.mail.yaml not composed).
    mail: supplied.apis?.mail === '' ? '' : absoluteApiUrl(supplied.apis?.mail || '/api/mail/'),
  },
  demoMode: demoOverride === 'true' || (demoOverride !== 'false' && supplied.demoMode === true),
});
